import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { fmtHours } from "@/lib/utils"
import { router } from "@inertiajs/react"
import { IconCalendar, IconChevronLeft, IconChevronRight } from "@tabler/icons-react"
import { addDays, format, parse } from "date-fns"
import { useEffect, useRef, useState } from "react"
import CalendarCreateDialog, { type EditingEntry } from "./CalendarCreateDialog"
import CalendarEntryBlock, { getEntryColor } from "./CalendarEntryBlock"

const HOUR_HEIGHT = 60
const TOTAL_HOURS = 24
const GRID_HEIGHT = HOUR_HEIGHT * TOTAL_HOURS
const SNAP_MINUTES = 15
const TIME_LABEL_WIDTH = 56
const DAYS_IN_WEEK = 5

interface TimeEntry {
  id: number
  user_id: number
  user_name: string
  user_avatar: string | null
  project_id: number | null
  project_name: string
  customer_name: string
  description: string
  entry_date: string
  hours: number | null
  started_at: string | null
  stopped_at: string | null
  billable: boolean
  running: boolean
  timer_display: string
  duration_hours: number | null
}

interface ProjectOption {
  id: number
  name: string
  customer_name: string
}

interface Props {
  entries: TimeEntry[]
  calendarDate: string
  weekStart: string
  projects: ProjectOption[]
  isAdmin?: boolean
  /** Where week-nav and date-picker should redirect. Defaults to /time_entries (personal). */
  basePath?: string
  /** Extra query params merged into every navigation. Lets the admin page keep `tab`/filters. */
  navParams?: Record<string, string | number | undefined>
  /** Hide create/edit/drag/resize interactions (admin view is read-only). */
  readOnly?: boolean
}

// Parse ISO timestamp and return LOCAL hours+minutes (browser timezone)
function isoToLocalMinutes(isoStr: string): number {
  const d = new Date(isoStr)
  return d.getHours() * 60 + d.getMinutes()
}

// Convert a local date string + local minutes into an ISO string (includes UTC offset)
function localMinutesToISO(dateStr: string, minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const d = new Date(`${dateStr}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`)
  return d.toISOString()
}

function minutesToPosition(minutes: number): number {
  return (minutes / 60) * HOUR_HEIGHT
}

function positionToMinutes(y: number): number {
  const raw = (y / HOUR_HEIGHT) * 60
  return Math.max(0, Math.min(Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES, 24 * 60))
}

function minutesToTimeStr(minutes: number): string {
  const clamped = Math.max(0, Math.min(minutes, 24 * 60 - 1))
  const h = Math.floor(clamped / 60)
  const m = clamped % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

function formatHourLabel(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`
}

/** Return the live hours for an entry, computing elapsed time for running trackers */
function liveEntryHours(entry: TimeEntry): number {
  if (entry.running && entry.started_at) {
    const startMs = new Date(entry.started_at).getTime()
    return Math.max((Date.now() - startMs) / 3_600_000, 0)
  }
  return entry.hours || entry.duration_hours || 0
}

// --- Overlap layout computation ---
function computeOverlapLayout(
  entries: { id: number; startMin: number; endMin: number }[],
): Map<number, { columnIndex: number; totalColumns: number }> {
  const result = new Map<number, { columnIndex: number; totalColumns: number }>()
  if (entries.length === 0) return result

  // Sort by start time, then by duration (longer first)
  const sorted = [...entries].sort((a, b) => a.startMin - b.startMin || b.endMin - b.startMin - (a.endMin - a.startMin))

  // Find connected overlap groups using a sweep approach
  const groups: (typeof sorted)[] = []
  let currentGroup = [sorted[0]]
  let groupEnd = sorted[0].endMin

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].startMin < groupEnd) {
      // Overlaps with current group
      currentGroup.push(sorted[i])
      groupEnd = Math.max(groupEnd, sorted[i].endMin)
    } else {
      groups.push(currentGroup)
      currentGroup = [sorted[i]]
      groupEnd = sorted[i].endMin
    }
  }
  groups.push(currentGroup)

  // For each group, assign columns greedily
  for (const group of groups) {
    const columns: number[][] = [] // columns[colIdx] = list of endMin for entries in that column

    for (const entry of group) {
      // Find first column where this entry fits (no overlap)
      let placed = false
      for (let col = 0; col < columns.length; col++) {
        const lastEnd = columns[col][columns[col].length - 1]
        if (entry.startMin >= lastEnd) {
          columns[col].push(entry.endMin)
          result.set(entry.id, { columnIndex: col, totalColumns: 0 })
          placed = true
          break
        }
      }
      if (!placed) {
        columns.push([entry.endMin])
        result.set(entry.id, { columnIndex: columns.length - 1, totalColumns: 0 })
      }
    }

    // Set totalColumns for all entries in this group
    const totalCols = columns.length
    for (const entry of group) {
      const layout = result.get(entry.id)!
      layout.totalColumns = totalCols
    }
  }

  return result
}

export default function DayCalendar({
  entries,
  calendarDate,
  weekStart,
  projects,
  isAdmin,
  basePath = "/time_entries",
  navParams = {},
  readOnly = false,
}: Props) {
  const gridRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [currentTimePos, setCurrentTimePos] = useState<number | null>(null)
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [createStartTime, setCreateStartTime] = useState("09:00")
  const [createEndTime, setCreateEndTime] = useState("10:00")
  const [createDate, setCreateDate] = useState(calendarDate)
  const [editingEntry, setEditingEntry] = useState<EditingEntry | null>(null)

  // All drag state in a ref to avoid stale closures
  const dragState = useRef({
    mode: "none" as "none" | "creating" | "moving" | "resizing",
    createDayIndex: 0,
    createStartMin: 0,
    createEndMin: 0,
    entryId: null as number | null,
    moveDayIndex: 0,
    moveStartMin: 0,
    moveDuration: 0,
    moveOffsetMin: 0,
    // Resize state
    resizeEdge: "bottom" as "top" | "bottom",
    resizeStartMin: 0,
    resizeEndMin: 0,
    resizeEntryDate: "",
  })
  const [, forceRender] = useState(0)
  function triggerRender() {
    forceRender((n) => n + 1)
  }

  const wsDate = parse(weekStart, "yyyy-MM-dd", new Date())
  const weekDays = Array.from({ length: DAYS_IN_WEEK }, (_, i) => addDays(wsDate, i))
  const weekDayStrs = weekDays.map((d) => format(d, "yyyy-MM-dd"))

  // Local "today" to highlight the right column
  const todayStr = format(new Date(), "yyyy-MM-dd")
  const todayDayIndex = weekDayStrs.indexOf(todayStr)

  // Group entries by day
  const entriesByDay: Record<string, TimeEntry[]> = {}
  for (const dayStr of weekDayStrs) entriesByDay[dayStr] = []
  for (const entry of entries) {
    if (entriesByDay[entry.entry_date]) entriesByDay[entry.entry_date].push(entry)
  }

  const weekTotalHours = entries.reduce((sum, e) => sum + liveEntryHours(e), 0)

  // Find the latest entry's project to use as default for new entries
  const latestProjectId =
    entries.length > 0
      ? String(
          [...entries]
            .filter((e) => e.project_id)
            .sort((a, b) => {
              if (a.started_at && b.started_at) return b.started_at.localeCompare(a.started_at)
              return b.entry_date.localeCompare(a.entry_date)
            })[0]?.project_id || "",
        )
      : ""

  // Tick counter — drives live updates for running tracker totals
  const [, setTick] = useState(0)
  const hasRunning = entries.some((e) => e.running)

  // Current time indicator — local time (matches the grid labels)
  // Also ticks the running-entry totals every minute
  useEffect(() => {
    function update() {
      if (todayDayIndex >= 0) {
        const n = new Date()
        setCurrentTimePos(minutesToPosition(n.getHours() * 60 + n.getMinutes()))
      } else {
        setCurrentTimePos(null)
      }
      if (hasRunning) setTick((t) => t + 1)
    }
    update()
    const interval = setInterval(update, 60000)
    return () => clearInterval(interval)
  }, [todayDayIndex, hasRunning])

  // Auto-scroll
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const allTimed = entries.filter((e) => e.started_at)
    if (allTimed.length > 0) {
      const firstStart = allTimed.reduce((min, e) => {
        const mins = isoToLocalMinutes(e.started_at!)
        return mins < min ? mins : min
      }, 24 * 60)
      container.scrollTop = Math.max(0, minutesToPosition(firstStart) - 40)
    } else {
      container.scrollTop = minutesToPosition(8 * 60) - 40
    }
  }, [weekStart]) // eslint-disable-line react-hooks/exhaustive-deps

  function buildNavParams(date: string) {
    const params: Record<string, string | number | undefined> = {
      view: "calendar",
      date,
      ...navParams,
    }
    return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))
  }

  function navigateWeek(direction: number) {
    router.get(basePath, buildNavParams(format(addDays(wsDate, direction * 7), "yyyy-MM-dd")), { preserveState: true })
  }

  function navigateToDate(d: Date) {
    router.get(basePath, buildNavParams(format(d, "yyyy-MM-dd")), { preserveState: true })
  }

  function getDayIndexFromX(clientX: number): number {
    const rect = gridRef.current?.getBoundingClientRect()
    if (!rect) return 0
    const dayWidth = (rect.width - TIME_LABEL_WIDTH) / DAYS_IN_WEEK
    return Math.max(0, Math.min(Math.floor((clientX - rect.left - TIME_LABEL_WIDTH) / dayWidth), DAYS_IN_WEEK - 1))
  }

  function getMinutesFromY(clientY: number): number {
    const rect = gridRef.current?.getBoundingClientRect()
    if (!rect) return 0
    return positionToMinutes(clientY - rect.top)
  }

  function handleGridMouseDown(e: React.MouseEvent) {
    if (readOnly) return
    if (e.button !== 0) return
    const target = e.target as HTMLElement
    if (target.closest("[data-entry-block]")) return
    if (!gridRef.current?.contains(target)) return
    if (dragState.current.mode !== "none") return

    const ds = dragState.current
    ds.mode = "creating"
    ds.createDayIndex = getDayIndexFromX(e.clientX)
    ds.createStartMin = getMinutesFromY(e.clientY)
    ds.createEndMin = ds.createStartMin + SNAP_MINUTES
    triggerRender()
  }

  function handleGridMouseMove(e: React.MouseEvent) {
    const ds = dragState.current
    if (ds.mode === "creating") {
      ds.createEndMin = getMinutesFromY(e.clientY)
      triggerRender()
    } else if (ds.mode === "moving") {
      ds.moveDayIndex = getDayIndexFromX(e.clientX)
      ds.moveStartMin = Math.max(0, getMinutesFromY(e.clientY) - ds.moveOffsetMin)
      triggerRender()
    } else if (ds.mode === "resizing") {
      const mins = getMinutesFromY(e.clientY)
      if (ds.resizeEdge === "top") {
        ds.resizeStartMin = Math.min(mins, ds.resizeEndMin - SNAP_MINUTES)
      } else {
        ds.resizeEndMin = Math.max(mins, ds.resizeStartMin + SNAP_MINUTES)
      }
      triggerRender()
    }
  }

  function handleGridMouseUp() {
    const ds = dragState.current
    if (ds.mode === "creating") {
      ds.mode = "none"
      const startMins = Math.min(ds.createStartMin, ds.createEndMin)
      const endMins = Math.max(ds.createStartMin, ds.createEndMin)
      if (endMins - startMins >= SNAP_MINUTES) {
        setCreateStartTime(minutesToTimeStr(startMins))
        setCreateEndTime(minutesToTimeStr(endMins))
        setCreateDate(weekDayStrs[ds.createDayIndex])
        setEditingEntry(null)
        setShowCreateDialog(true)
      }
      triggerRender()
    } else if (ds.mode === "moving" && ds.entryId !== null) {
      ds.mode = "none"
      const newDate = weekDayStrs[ds.moveDayIndex]
      const startedAt = localMinutesToISO(newDate, ds.moveStartMin)
      const stoppedAt = localMinutesToISO(newDate, ds.moveStartMin + ds.moveDuration)

      router.patch(`/time_entries/${ds.entryId}`, {
        time_entry: {
          entry_date: newDate,
          started_at: startedAt,
          stopped_at: stoppedAt,
          hours: (ds.moveDuration / 60).toFixed(2),
        },
        view: "calendar",
        date: weekStart,
      })
      ds.entryId = null
      triggerRender()
    } else if (ds.mode === "resizing" && ds.entryId !== null) {
      ds.mode = "none"
      const duration = ds.resizeEndMin - ds.resizeStartMin
      const startedAt = localMinutesToISO(ds.resizeEntryDate, ds.resizeStartMin)
      const stoppedAt = localMinutesToISO(ds.resizeEntryDate, ds.resizeEndMin)

      router.patch(`/time_entries/${ds.entryId}`, {
        time_entry: {
          started_at: startedAt,
          stopped_at: stoppedAt,
          hours: (duration / 60).toFixed(2),
        },
        view: "calendar",
        date: weekStart,
      })
      ds.entryId = null
      triggerRender()
    }
  }

  function handleEntryDragStart(
    entryId: number,
    dayIndex: number,
    startMin: number,
    durationMin: number,
    offsetMin: number,
  ) {
    const ds = dragState.current
    ds.mode = "moving"
    ds.entryId = entryId
    ds.moveDayIndex = dayIndex
    ds.moveStartMin = startMin
    ds.moveDuration = durationMin
    ds.moveOffsetMin = offsetMin
    triggerRender()
  }

  function handleEntryResizeStart(
    entryId: number,
    edge: "top" | "bottom",
    startMin: number,
    endMin: number,
    entryDate: string,
  ) {
    const ds = dragState.current
    ds.mode = "resizing"
    ds.entryId = entryId
    ds.resizeEdge = edge
    ds.resizeStartMin = startMin
    ds.resizeEndMin = endMin
    ds.resizeEntryDate = entryDate
    triggerRender()
  }

  function handleEditEntry(entry: {
    id: number
    project_id: number | null
    description: string
    billable: boolean
    started_at: string | null
    stopped_at: string | null
    entry_date: string
  }) {
    setEditingEntry({
      id: entry.id,
      project_id: entry.project_id,
      description: entry.description,
      billable: entry.billable,
      started_at: entry.started_at,
      stopped_at: entry.stopped_at,
    })

    if (entry.started_at) {
      const startD = new Date(entry.started_at)
      const endD = entry.stopped_at ? new Date(entry.stopped_at) : startD
      setCreateStartTime(
        `${String(startD.getHours()).padStart(2, "0")}:${String(startD.getMinutes()).padStart(2, "0")}`,
      )
      setCreateEndTime(`${String(endD.getHours()).padStart(2, "0")}:${String(endD.getMinutes()).padStart(2, "0")}`)
    } else {
      setCreateStartTime("09:00")
      setCreateEndTime("10:00")
    }

    setCreateDate(entry.entry_date)
    setShowCreateDialog(true)
  }

  function handleDialogOpenChange(open: boolean) {
    setShowCreateDialog(open)
    if (!open) setEditingEntry(null)
  }

  const ds = dragState.current
  const isCreating = ds.mode === "creating"
  const isMoving = ds.mode === "moving"
  const isResizing = ds.mode === "resizing"

  const selStartMin = Math.min(ds.createStartMin, ds.createEndMin)
  const selEndMin = Math.max(ds.createStartMin, ds.createEndMin)
  const selTop = minutesToPosition(selStartMin)
  const selHeight = minutesToPosition(selEndMin - selStartMin)

  const ghostTop = minutesToPosition(ds.moveStartMin)
  const ghostHeight = minutesToPosition(ds.moveDuration)

  function colLeft(dayIndex: number) {
    return `calc(${TIME_LABEL_WIDTH}px + ${(dayIndex / DAYS_IN_WEEK) * 100}% - ${(dayIndex / DAYS_IN_WEEK) * TIME_LABEL_WIDTH}px)`
  }
  function colLeftInset(dayIndex: number) {
    return `calc(${TIME_LABEL_WIDTH}px + ${(dayIndex / DAYS_IN_WEEK) * 100}% - ${(dayIndex / DAYS_IN_WEEK) * TIME_LABEL_WIDTH}px + 2px)`
  }
  const colWidthFull = `calc(${100 / DAYS_IN_WEEK}% - ${TIME_LABEL_WIDTH / DAYS_IN_WEEK}px)`
  const colWidthInset = `calc(${100 / DAYS_IN_WEEK}% - ${TIME_LABEL_WIDTH / DAYS_IN_WEEK}px - 4px)`

  return (
    <div className="flex flex-col gap-3">
      {/* Week navigation header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateWeek(-1)}>
            <IconChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateWeek(1)}>
            <IconChevronRight className="h-4 w-4" />
          </Button>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="text-base font-semibold">
                <IconCalendar className="mr-2 h-4 w-4" />
                {format(wsDate, "MMM d")} – {format(addDays(wsDate, 4), "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={parse(calendarDate, "yyyy-MM-dd", new Date())}
                onSelect={(d) => {
                  if (d) {
                    navigateToDate(d)
                    setDatePickerOpen(false)
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {todayDayIndex < 0 && (
            <Button variant="outline" size="sm" onClick={() => navigateToDate(new Date())}>
              Today
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-muted-foreground text-sm">
            Week total: <span className="text-foreground font-medium">{fmtHours(weekTotalHours)}</span>
          </div>
        </div>
      </div>

      {/* Day column headers */}
      <div className="flex" style={{ paddingLeft: `${TIME_LABEL_WIDTH}px` }}>
        {weekDays.map((day, i) => {
          const dayStr = weekDayStrs[i]
          const dayEntries = entriesByDay[dayStr] || []
          const dayTotal = dayEntries.reduce((sum, e) => sum + liveEntryHours(e), 0)
          const isTodayCol = dayStr === todayStr
          return (
            <div
              key={dayStr}
              className={`flex-1 text-center border-l first:border-l-0 px-1 py-1.5 ${isTodayCol ? "bg-primary/5" : ""}`}
            >
              <div className={`text-xs font-medium ${isTodayCol ? "text-primary" : "text-muted-foreground"}`}>
                {format(day, "EEE")}
              </div>
              <div className={`text-lg font-semibold leading-tight ${isTodayCol ? "text-primary" : ""}`}>
                {format(day, "d")}
              </div>
              {dayTotal > 0 && <div className="text-[10px] text-muted-foreground">{fmtHours(dayTotal)}</div>}
            </div>
          )
        })}
      </div>

      {/* Untimed entries (e.g. CSV imports without clock times) — pills above the grid */}
      {(() => {
        const manual = entries.filter((e) => !e.started_at && !e.running)
        if (manual.length === 0) return null
        return (
          <div className="flex flex-wrap gap-2">
            {manual.map((entry) => {
              const color = getEntryColor(entry.project_id || 0)
              return (
                <div
                  key={entry.id}
                  className={`border-l-3 rounded-md px-3 py-1.5 text-xs hover:shadow-sm transition-shadow ${color.border} ${color.bg}`}
                >
                  <span className="font-medium">{entry.project_name}</span>
                  <span className="text-muted-foreground ml-2">{entry.description}</span>
                  <span className="text-muted-foreground ml-2">
                    {entry.hours != null ? fmtHours(entry.hours) : "—"}
                  </span>
                </div>
              )
            })}
          </div>
        )
      })()}

      {/* Workload breakdown bar */}
      {(() => {
        const projectHours: Record<string, { projectId: number | null; name: string; hours: number }> = {}
        for (const entry of entries) {
          const h = liveEntryHours(entry)
          if (h <= 0) continue
          const key = String(entry.project_id ?? "none")
          if (!projectHours[key]) {
            projectHours[key] = { projectId: entry.project_id, name: entry.project_name, hours: 0 }
          }
          projectHours[key].hours += h
        }
        const segments = Object.values(projectHours).sort((a, b) => b.hours - a.hours)
        if (segments.length === 0) return null
        return (
          <TooltipProvider delayDuration={100}>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex h-6 rounded-md overflow-hidden border">
                {segments.map((seg) => {
                  const pct = (seg.hours / weekTotalHours) * 100
                  const color = getEntryColor(seg.projectId || 0)
                  return (
                    <Tooltip key={seg.projectId ?? "none"}>
                      <TooltipTrigger asChild>
                        <div
                          className={`${color.solid} h-full transition-all relative group/seg flex items-center justify-center overflow-hidden`}
                          style={{ width: `${pct}%` }}
                        >
                          {pct > 10 && (
                            <span className="text-white text-[10px] font-medium truncate px-1">{seg.name}</span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-medium">{seg.name}</p>
                        <p className="text-muted-foreground text-xs">
                          {fmtHours(seg.hours)} ({pct.toFixed(0)}%)
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          </TooltipProvider>
        )
      })()}

      {/* Hourly grid */}
      <div
        ref={scrollContainerRef}
        className="border rounded-lg overflow-y-auto bg-background"
        style={{ height: "calc(100vh - 220px)" }}
      >
        <div
          ref={gridRef}
          className="relative select-none"
          style={{
            height: `${GRID_HEIGHT}px`,
            cursor: isCreating ? "crosshair" : isMoving ? "grabbing" : isResizing ? "ns-resize" : undefined,
          }}
          onMouseDown={handleGridMouseDown}
          onMouseMove={handleGridMouseMove}
          onMouseUp={handleGridMouseUp}
          onMouseLeave={handleGridMouseUp}
        >
          {/* Hour lines and labels */}
          {Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => (
            <div key={`hour-${i}`}>
              <div
                className="absolute left-0 text-right pr-2 text-[11px] text-muted-foreground -translate-y-1/2"
                style={{ top: `${i * HOUR_HEIGHT}px`, width: `${TIME_LABEL_WIDTH}px` }}
              >
                {formatHourLabel(i)}
              </div>
              <div
                className="absolute right-0 border-t border-border/50"
                style={{ top: `${i * HOUR_HEIGHT}px`, left: `${TIME_LABEL_WIDTH}px` }}
              />
              {i < TOTAL_HOURS && (
                <div
                  className="absolute right-0 border-t border-border/20 border-dashed"
                  style={{ top: `${i * HOUR_HEIGHT + HOUR_HEIGHT / 2}px`, left: `${TIME_LABEL_WIDTH}px` }}
                />
              )}
            </div>
          ))}

          {/* Day column separators */}
          {Array.from({ length: DAYS_IN_WEEK - 1 }, (_, i) => (
            <div
              key={`col-${i}`}
              className="absolute top-0 bottom-0 border-l border-border/30"
              style={{ left: colLeft(i + 1) }}
            />
          ))}

          {/* Today column highlight */}
          {todayDayIndex >= 0 && (
            <div
              className="absolute top-0 bottom-0 bg-primary/[0.03]"
              style={{ left: colLeft(todayDayIndex), width: colWidthFull }}
            />
          )}

          {/* Current time indicator */}
          {currentTimePos !== null && todayDayIndex >= 0 && (
            <div
              className="absolute z-20 flex items-center"
              style={{
                top: `${currentTimePos}px`,
                left: `calc(${colLeft(todayDayIndex)} - 5px)`,
                width: `calc(${colWidthFull} + 5px)`,
              }}
            >
              <div className="h-2.5 w-2.5 rounded-full bg-red-500 shrink-0" />
              <div className="flex-1 border-t-2 border-red-500" />
            </div>
          )}

          {/* Drag-to-create selection */}
          {isCreating && selHeight > 0 && (
            <div
              className="absolute bg-primary/10 border border-primary/30 rounded z-10 pointer-events-none"
              style={{
                top: `${selTop}px`,
                height: `${selHeight}px`,
                left: colLeftInset(ds.createDayIndex),
                width: colWidthInset,
              }}
            />
          )}

          {/* Drag-move ghost */}
          {isMoving && ds.entryId !== null && (
            <div
              className="absolute bg-primary/10 border-2 border-primary/40 border-dashed rounded z-30 pointer-events-none px-2 py-0.5 overflow-hidden"
              style={{
                top: `${ghostTop}px`,
                height: `${ghostHeight}px`,
                left: colLeftInset(ds.moveDayIndex),
                width: colWidthInset,
              }}
            >
              <p className="text-xs font-medium text-primary/70 truncate">
                {minutesToTimeStr(ds.moveStartMin)} – {minutesToTimeStr(ds.moveStartMin + ds.moveDuration)}
              </p>
              {ds.moveDuration >= 30 && <p className="text-[11px] text-primary/50">{fmtHours(ds.moveDuration / 60)}</p>}
            </div>
          )}

          {/* Entry blocks per day */}
          {weekDays.map((_, dayIndex) => {
            const dayStr = weekDayStrs[dayIndex]
            const dayEntries = entriesByDay[dayStr] || []

            // Compute timed entries with their minute ranges
            const timedEntries = dayEntries
              .filter((e) => e.started_at && !e.running)
              .map((entry) => {
                const startMin = isoToLocalMinutes(entry.started_at!)
                const endMin = entry.stopped_at
                  ? isoToLocalMinutes(entry.stopped_at)
                  : startMin + (entry.hours || 1) * 60
                return { id: entry.id, startMin, endMin }
              })

            // Compute overlap layout
            const overlapLayout = computeOverlapLayout(timedEntries)

            return (
              <div key={dayStr}>
                {dayEntries
                  .filter((e) => e.started_at && !e.running)
                  .map((entry) => {
                    const startMins = isoToLocalMinutes(entry.started_at!)
                    const endMins = entry.stopped_at
                      ? isoToLocalMinutes(entry.stopped_at)
                      : startMins + (entry.hours || 1) * 60

                    // If this entry is being resized, use the drag state for display
                    const isBeingResized = isResizing && ds.entryId === entry.id
                    const displayStart = isBeingResized ? ds.resizeStartMin : startMins
                    const displayEnd = isBeingResized ? ds.resizeEndMin : endMins

                    const top = minutesToPosition(displayStart)
                    const height = minutesToPosition(displayEnd - displayStart)
                    const isBeingDragged = isMoving && ds.entryId === entry.id

                    const layout = overlapLayout.get(entry.id) || { columnIndex: 0, totalColumns: 1 }

                    return (
                      <CalendarEntryBlock
                        key={entry.id}
                        entry={entry}
                        top={top}
                        height={height}
                        calendarDate={dayStr}
                        dayIndex={dayIndex}
                        totalDays={DAYS_IN_WEEK}
                        timeLabelWidth={TIME_LABEL_WIDTH}
                        columnIndex={layout.columnIndex}
                        totalColumns={layout.totalColumns}
                        faded={isBeingDragged}
                        isAdmin={isAdmin}
                        onDragStart={
                          readOnly
                            ? () => {}
                            : (offsetMin) =>
                                handleEntryDragStart(entry.id, dayIndex, startMins, endMins - startMins, offsetMin)
                        }
                        onResizeStart={
                          readOnly
                            ? () => {}
                            : (edge) => handleEntryResizeStart(entry.id, edge, startMins, endMins, dayStr)
                        }
                        onEdit={readOnly ? () => {} : () => handleEditEntry({ ...entry, entry_date: dayStr })}
                        displayTimeRange={
                          isBeingResized
                            ? `${minutesToTimeStr(displayStart)} – ${minutesToTimeStr(displayEnd)}`
                            : undefined
                        }
                        displayDuration={isBeingResized ? fmtHours((displayEnd - displayStart) / 60) : undefined}
                      />
                    )
                  })}
                {dayEntries
                  .filter((e) => e.running && e.started_at)
                  .map((entry) => {
                    const startMins = isoToLocalMinutes(entry.started_at!)
                    const n = new Date()
                    const nowMins = n.getHours() * 60 + n.getMinutes()
                    const top = minutesToPosition(startMins)
                    const height = minutesToPosition(Math.max(nowMins - startMins, 15))
                    const isSmall = height < 40
                    const color = getEntryColor(entry.project_id || 0)
                    return (
                      <div
                        key={entry.id}
                        data-entry-block
                        className={`absolute border-l-3 rounded-md px-2 py-0.5 overflow-hidden ${color.border} ${color.bg} animate-pulse`}
                        style={{
                          top: `${top}px`,
                          height: `${Math.max(height, 18)}px`,
                          left: colLeftInset(dayIndex),
                          width: colWidthInset,
                        }}
                      >
                        {isSmall ? (
                          <p className="truncate text-[11px] font-medium leading-tight">
                            {entry.project_name} — {entry.description}
                          </p>
                        ) : (
                          <>
                            <p className="truncate text-xs font-medium leading-tight">
                              {entry.project_name} <span className="text-muted-foreground">(running)</span>
                            </p>
                            <p className="text-muted-foreground truncate text-[11px] leading-tight">
                              {entry.description}
                            </p>
                          </>
                        )}
                      </div>
                    )
                  })}
              </div>
            )
          })}
        </div>
      </div>

      <CalendarCreateDialog
        open={showCreateDialog}
        onOpenChange={handleDialogOpenChange}
        startTime={createStartTime}
        endTime={createEndTime}
        calendarDate={createDate}
        weekStart={weekStart}
        projects={projects}
        editingEntry={editingEntry}
        defaultProjectId={latestProjectId}
      />
    </div>
  )
}
